import type React from "react";
import type { AutoCommentConfig } from "../../../../types/core";
import { DEFAULT_AUTO_COMMENT_CONFIG } from "../../../../types/core";

interface PreferenceAutoCommentTabProps {
	autoCommentConfig: Partial<AutoCommentConfig>;
	onUpdateAutoCommentConfig: (
		config: Partial<AutoCommentConfig>,
	) => Promise<void>;
}

/**
 * スライダーUIコンポーネント（共通）
 */
const SettingSlider: React.FC<{
	id: string;
	label: string;
	description: string;
	value: number;
	min: number;
	max: number;
	step: number;
	displayValue: string;
	onChange: (val: number) => void;
}> = ({
	id,
	label,
	description,
	value,
	min,
	max,
	step,
	displayValue,
	onChange,
}) => (
	<div className="flex flex-col gap-2 bg-bg-main p-3 rounded-lg border border-border-main">
		<div className="flex items-center justify-between gap-4">
			<label
				htmlFor={id}
				className="text-sm font-medium text-text-main cursor-pointer"
			>
				{label}
			</label>
			<span className="text-sm font-mono text-primary min-w-[5rem] text-right">
				{displayValue}
			</span>
		</div>
		<span className="text-xs text-text-dim">{description}</span>
		<input
			type="range"
			id={id}
			min={min}
			max={max}
			step={step}
			value={value}
			className="w-full accent-primary cursor-pointer"
			onChange={(e) => onChange(Number(e.target.value))}
		/>
		<div className="flex justify-between text-xs text-text-dim">
			<span>{min < 1000 ? `${min}` : `${min / 1000}s`}</span>
			<span>{max < 1000 ? `${max}` : `${max / 1000}s`}</span>
		</div>
	</div>
);

export const PreferenceAutoCommentTab: React.FC<
	PreferenceAutoCommentTabProps
> = ({ autoCommentConfig, onUpdateAutoCommentConfig }) => {
	// 保存値とデフォルト値をマージして表示する
	const config: AutoCommentConfig = {
		...DEFAULT_AUTO_COMMENT_CONFIG,
		...autoCommentConfig,
	};

	const handleToggle = async () => {
		await onUpdateAutoCommentConfig({ enabled: !config.enabled });
	};

	const handleSilenceChange = async (val: number) => {
		await onUpdateAutoCommentConfig({ silenceThresholdMs: val });
	};

	const handleMinIntervalChange = async (val: number) => {
		await onUpdateAutoCommentConfig({ minIntervalMs: val });
	};

	const handleMaxIntervalChange = async (val: number) => {
		await onUpdateAutoCommentConfig({ maxIntervalMs: val });
	};

	const handleMinNewCharsChange = async (val: number) => {
		await onUpdateAutoCommentConfig({ minNewChars: val });
	};

	return (
		<div className="flex flex-col gap-8">
			<h3 className="m-0 mb-4 text-[1.1rem] text-primary border-b border-border-main pb-2">
				自動コメント生成
			</h3>

			{/* ON/OFFトグル */}
			<div className="flex flex-col gap-3 bg-bg-main p-3 rounded-lg border border-border-main mb-3">
				<div className="flex items-center justify-between gap-4">
					<div className="flex flex-col gap-1">
						<label
							htmlFor="auto-comment-toggle"
							className="text-sm font-medium text-text-main cursor-pointer"
						>
							自動コメント生成
						</label>
						<span className="text-xs text-text-dim">
							沈黙を検知して、AIが自動的にコメントを生成します
						</span>
					</div>
					<button
						id="auto-comment-toggle"
						type="button"
						role="switch"
						aria-checked={config.enabled}
						className={[
							"relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 active:scale-95",
							"focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main",
							config.enabled ? "bg-primary" : "bg-border-main",
						].join(" ")}
						onClick={handleToggle}
					>
						<span
							className={[
								"inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200",
								config.enabled ? "translate-x-6" : "translate-x-1",
							].join(" ")}
						/>
					</button>
				</div>
				{config.enabled && (
					<p className="text-xs text-green-400 mt-1">
						✅ 自動コメント生成が有効です。話し終わった後にAIがコメントします。
					</p>
				)}
			</div>

			{/* 詳細設定（ONのときのみ表示） */}
			{config.enabled && (
				<div className="flex flex-col gap-4">
					<h4 className="m-0 text-[0.95rem] text-text-main font-semibold">
						タイミング設定
					</h4>

					<SettingSlider
						id="auto-comment-silence-threshold"
						label="沈黙検知時間"
						description="この時間、音声がなければコメント候補になります。短すぎると話の途中で生成されます。"
						value={config.silenceThresholdMs}
						min={1000}
						max={5000}
						step={500}
						displayValue={`${config.silenceThresholdMs / 1000}秒`}
						onChange={handleSilenceChange}
					/>

					<SettingSlider
						id="auto-comment-min-interval"
						label="生成インターバル（最短）"
						description="コメントが連続して生成されないための最短待機時間です。"
						value={config.minIntervalMs}
						min={10000}
						max={60000}
						step={5000}
						displayValue={`${config.minIntervalMs / 1000}秒`}
						onChange={handleMinIntervalChange}
					/>

					<SettingSlider
						id="auto-comment-max-interval"
						label="生成インターバル（最長）"
						description="この時間以上コメントがない場合、条件を無視して強制生成します。"
						value={config.maxIntervalMs}
						min={30000}
						max={180000}
						step={10000}
						displayValue={`${config.maxIntervalMs / 1000}秒`}
						onChange={handleMaxIntervalChange}
					/>

					<SettingSlider
						id="auto-comment-min-new-chars"
						label="最低新規テキスト量"
						description="前回コメント以降にこの文字数以上話さないとコメントしません。短い相槌などでの生成を防ぎます。"
						value={config.minNewChars}
						min={10}
						max={100}
						step={10}
						displayValue={`${config.minNewChars}文字`}
						onChange={handleMinNewCharsChange}
					/>
				</div>
			)}

			{/* 説明 */}
			<div className="bg-bg-main p-3 rounded-lg border border-border-main text-xs text-text-dim flex flex-col gap-1">
				<p className="font-semibold text-text-main mb-1">💡 自動生成の仕組み</p>
				<p>・ 沈黙が「沈黙検知時間」を超えたとき、自動的にトリガーされます</p>
				<p>・ 「最低新規テキスト量」を満たしていない場合は生成しません</p>
				<p>・ 「最短インターバル」以内に連続生成されることはありません</p>
				<p>・ 手動の「コメント生成」ボタンは引き続き利用できます</p>
			</div>
		</div>
	);
};
